import React from 'react'
import type { Block, Variable } from '../../editor/types'

interface NumberInputBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  variables: Variable[]
  onSetVariableValue: (name: string, value: number | string | boolean) => void
}

export default function NumberInputBlock({ block, variableValues, variables, onSetVariableValue }: NumberInputBlockProps) {
  const varName = block.variable || ''
  const variable = variables.find((v) => v.name === varName)
  const min = variable?.min
  const max = variable?.max
  const step = variable?.step ?? 1
  const value = Number(variableValues[varName] ?? variable?.default ?? 0)
  const config = (block.config || {}) as Record<string, unknown>
  const placeholder = (config.placeholder as string) || '0'
  const suffix = (config.suffix as string) || ''

  const accent = block.accent || 'var(--accent, #5048e5)'

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '' || raw === '-') {
      onSetVariableValue(varName, 0)
      return
    }
    let num = Number(raw)
    if (isNaN(num)) return
    if (min !== undefined && num < min) num = min
    if (max !== undefined && num > max) num = max
    onSetVariableValue(varName, num)
  }

  return (
    <div className="flex flex-col gap-3">
      {block.label && (
        <label className="text-sm font-semibold text-slate-900">{block.label}</label>
      )}
      <div className="relative flex items-center">
        <input
          type="number"
          value={value}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          className="w-full bg-white border-2 rounded-xl py-3 pr-4 text-2xl font-mono font-bold outline-none transition-all focus:ring-2 focus:ring-offset-1"
          style={{
            borderColor: accent,
            color: accent,
            '--tw-ring-color': accent,
            paddingLeft: suffix ? '2.5rem' : '1rem',
          } as React.CSSProperties}
        />
        {suffix && (
          <span
            className="absolute left-4 text-lg font-semibold pointer-events-none"
            style={{ color: accent }}
          >
            {suffix}
          </span>
        )}
      </div>
      {(min !== undefined || max !== undefined) && (
        <div className="flex justify-between text-[10px] text-slate-400 font-bold uppercase tracking-widest">
          {min !== undefined && <span>Min: {min}</span>}
          {max !== undefined && <span className="ml-auto">Max: {max}</span>}
        </div>
      )}
    </div>
  )
}
